import React, { useState, useRef, useEffect } from 'react';
import { MdSearch, MdFilterList } from 'react-icons/md';
import { formatDate } from '../utils/units';
import './SystemLogs.css';

const SystemLogs = ({ logs = [], type = 'system', title = 'System Logs' }) => {
    const [filter, setFilter] = useState('all'); // all, error, warn, info
    const [searchTerm, setSearchTerm] = useState('');
    const logsEndRef = useRef(null);

    // Auto-scroll to bottom
    useEffect(() => {
        if (logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs]);

    // Get log color based on level
    const getLogColor = (level) => {
        switch (level?.toLowerCase()) {
            case 'error':
            case 'crit':
                return 'text-danger';
            case 'warn':
            case 'warning':
                return 'text-warning';
            case 'info':
                return 'text-info';
            case 'debug':
                return 'text-muted';
            default:
                return 'text-light';
        }
    };

    // Get log icon
    const getLogIcon = (level) => {
        switch (level?.toLowerCase()) {
            case 'error':
            case 'crit':
                return '❌';
            case 'warn':
            case 'warning':
                return '⚠️';
            case 'info':
                return 'ℹ️';
            case 'debug':
                return '🐛';
            default:
                return '📝';
        }
    };

    // Filter logs
    const filteredLogs = logs.filter(log => {
        // Filter by level
        if (filter !== 'all') {
            const logLevel = log.level?.toLowerCase();
            if (filter === 'error' && logLevel !== 'error' && logLevel !== 'crit') return false;
            if (filter === 'warn' && logLevel !== 'warn' && logLevel !== 'warning') return false;
            if (filter === 'info' && logLevel !== 'info') return false;
        }

        // Filter by search term
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const message = log.message?.toLowerCase() || '';
            const processName = log.processName?.toLowerCase() || '';
            const path = log.path?.toLowerCase() || '';

            return message.includes(searchLower) ||
                processName.includes(searchLower) ||
                path.includes(searchLower);
        }

        return true;
    });

    // Format timestamp
    const formatTime = (timestamp) => {
        return formatDate(timestamp, "time-only");
    };

    // Render log line based on type
    const renderLogLine = (log, index) => {
        const level = log.level || 'info';

        if (type === 'pm2') {
            return (
                <div key={index} className="log-line">
                    <span className="log-icon">{getLogIcon(level)}</span>
                    <span className="log-time">{formatTime(log.timestamp)}</span>
                    <span className={`log-level ${getLogColor(level)}`}>
                        [{level.toUpperCase()}]
                    </span>
                    <span className="log-process text-primary">[{log.processName}]</span>
                    <span className="log-message">{log.message}</span>
                </div>
            );
        } else if (type === 'nginx-access') {
            return (
                <div key={index} className="log-line">
                    <span className="log-icon">{log.status >= 400 ? '❌' : '✅'}</span>
                    <span className="log-time">{formatTime(log.timestamp)}</span>
                    <span className={`log-status ${log.status >= 400 ? 'text-danger' : 'text-success'}`}>
                        [{log.status}]
                    </span>
                    <span className="log-method text-info">{log.method}</span>
                    <span className="log-path">{log.path}</span>
                    <span className="log-ip text-muted">- {log.ip}</span>
                </div>
            );
        } else if (type === 'nginx-error') {
            return (
                <div key={index} className="log-line">
                    <span className="log-icon">{getLogIcon(level)}</span>
                    <span className="log-time">{formatTime(log.timestamp)}</span>
                    <span className={`log-level ${getLogColor(level)}`}>
                        [{level.toUpperCase()}]
                    </span>
                    {log.pid && <span className="log-pid text-muted">[PID:{log.pid}]</span>}
                    <span className="log-message">{log.message}</span>
                </div>
            );
        } else {
            // Generic log format
            return (
                <div key={index} className="log-line">
                    <span className="log-icon">{getLogIcon(level)}</span>
                    <span className="log-time">{formatTime(log.timestamp)}</span>
                    <span className={`log-level ${getLogColor(level)}`}>
                        [{level.toUpperCase()}]
                    </span>
                    <span className="log-message">{log.message || JSON.stringify(log)}</span>
                </div>
            );
        }
    };

    return (
        <div className="system-logs-container">
            {/* Header with filters */}
            <div className="logs-header">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">
                        <MdFilterList className="me-2" />
                        {title}
                    </h6>
                    <span className="badge bg-secondary rounded-pill">
                        {filteredLogs.length} / {logs.length} logs
                    </span>
                </div>

                {/* Filters */}
                <div className="d-flex gap-2 mb-3 flex-wrap">
                    <div className="btn-group btn-group-sm" role="group">
                        <button
                            className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setFilter('all')}
                        >
                            All
                        </button>
                        <button
                            className={`btn ${filter === 'info' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setFilter('info')}
                        >
                            Info
                        </button>
                        <button
                            className={`btn ${filter === 'warn' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setFilter('warn')}
                        >
                            Warnings
                        </button>
                        <button
                            className={`btn ${filter === 'error' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setFilter('error')}
                        >
                            Errors
                        </button>
                    </div>

                    {/* Search */}
                    <div className="input-group input-group-sm flex-grow-1" style={{ maxWidth: '300px' }}>
                        <span className="input-group-text">
                            <MdSearch />
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search logs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Logs display */}
            <div className="logs-terminal">
                {filteredLogs.length === 0 ? (
                    <div className="no-logs text-center text-muted py-5">
                        {logs.length === 0 ? (
                            <>
                                <p>No logs available yet...</p>
                                <small>Logs will appear here in real-time</small>
                            </>
                        ) : (
                            <>
                                <p>No logs match your filters</p>
                                <small>Try adjusting your search or filter criteria</small>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        {filteredLogs.slice().reverse().map((log, index) => renderLogLine(log, index))}
                        <div ref={logsEndRef} />
                    </>
                )}
            </div>
        </div>
    );
};

export default SystemLogs;
